import Button from '@/components/Buttons/Button';
import useUploadCloneVoice, { uploadedCloneVoice } from '@/pages/editor/components/useUploadCloneVoice';
import { SupportedLanguage } from '@/utils/types';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useRecoilState } from 'recoil';
import record from './images/record.svg';
import recording from './images/recording.svg';
import retry from './images/retry.svg';
import stop from './images/stop.svg';
import RecordingPlayer from './RecordingPlayer';

const SubmitButton = styled(Button)`
    height: 48px;
    width: 100%;
`;

const RecordingControlsContainer = styled.div`
    width: 100%;
    height: 48px;
    display: flex;
    justify-content: space-between;
`;

const ControlActions = styled.div`
    display: flex;
    height: 100%;
    gap: 4px;
`;

const StopRecordingButton = styled(Button)`
    background: var(--Danger-Main, #ed254e);
    height: 100%;
`;

const RetakeButton = styled(Button)`
    height: 100%;
`;

const CloneVoiceButton = styled(Button)`
    height: 100%;
`;

const RecordTimeIndication = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    gap: 8px;
`;

const padDigits = (num: number): string => {
    return num.toString().padStart(2, '0');
};

const formatSeconds = (milliseconds: number): string => {
    const seconds: number = Math.floor(milliseconds / 1000);
    const minutes: number = Math.floor(seconds / 60);
    const remainingSeconds: number = seconds % 60;
    return `${padDigits(minutes)}:${padDigits(remainingSeconds)}`;
};

export interface Props {
    voiceName: string;
    language: SupportedLanguage;
    onSubmit: (args: { file: string; name: string; language?: SupportedLanguage }) => void;
}

const DEFAULT_AUDIO_DATA = { data: null, duration: 0 };

function RecordingControls({ voiceName, language, onSubmit }: Props) {
    const [recorded, setRecorded] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const recordingStartDateRef = useRef<Date | null>(null);
    const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const { startRecording, stopRecording, isRecording, recordingBlob } = useAudioRecorder({
        noiseSuppression: true,
        echoCancellation: true,
    });
    const [isCanceled, setIsCanceled] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState(false);
    const { upload, reset } = useUploadCloneVoice();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [audioData, setAudioData] = useRecoilState(uploadedCloneVoice);

    const clone = async () => {
        setIsLoading(true);

        onSubmit({
            file: audioData!.data as string,
            name: voiceName,
            language,
        });
        setIsLoading(false);
        reset();
    };

    const onRecordCompleted = async (recordingBlob: Blob) => {
        setIsUploading(true);
        try {
            await upload(undefined, recordingBlob);
        } catch {}
        setIsUploading(false);
    };
    useEffect(() => {
        if (recordingBlob && !isCanceled) {
            onRecordCompleted(recordingBlob);
        }
        setIsCanceled(false);
    }, [recordingBlob]);

    function handleRecord() {
        startRecording();
        recordingStartDateRef.current = new Date();
        recordingIntervalRef.current = setInterval(() => {
            if (!recordingStartDateRef.current) {
                return;
            }
            const diffInMilliseconds = new Date().getTime() - recordingStartDateRef.current.getTime();
            const diffInMinutes = Math.abs(diffInMilliseconds) / (1000 * 60);

            if (diffInMinutes > 2) {
                handleStopRecording();
            }
            setRecordingDuration(diffInMilliseconds);
        }, 1000);
    }

    function handleStopRecording() {
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
        }
        setRecordingDuration(0);
        setIsUploading(true);
        stopRecording();
        setRecorded(true);
    }

    function handleRetake() {
        setAudioData(DEFAULT_AUDIO_DATA);
        if (isRecording) {
            setIsCanceled(true);
        }
        reset();
        handleStopRecording();
        setTimeout(() => {
            handleRecord();
        }, 100);
    }

    if (!isRecording) {
        if (!recorded) {
            return (
                <SubmitButton variant="contained" color="primary" startIcon={<img src={record} />} onClick={handleRecord}>
                    Record
                </SubmitButton>
            );
        }

        return (
            <RecordingControlsContainer>
                <RecordingPlayer isUploading={isUploading} duration={audioData?.duration || 0} listenUrl={audioData?.listenUrl} />
                <ControlActions>
                    <RetakeButton variant="text" color="secondary" startIcon={<img src={retry} />} onClick={handleRetake}>
                        Retake
                    </RetakeButton>
                    <CloneVoiceButton
                        disabled={isUploading || voiceName === ''}
                        isLoading={isLoading}
                        variant="contained"
                        color="primary"
                        onClick={clone}>
                        Clone voice
                    </CloneVoiceButton>
                </ControlActions>
            </RecordingControlsContainer>
        );
    }

    return (
        <RecordingControlsContainer>
            <RecordTimeIndication>
                <img src={recording} />
                <Typography variant="h4">{formatSeconds(recordingDuration)}</Typography>
            </RecordTimeIndication>
            <ControlActions>
                <RetakeButton variant="text" color="secondary" startIcon={<img src={retry} />} onClick={handleRetake}>
                    Retake
                </RetakeButton>
                <StopRecordingButton startIcon={<img src={stop} />} color="error" onClick={handleStopRecording}>
                    Stop
                </StopRecordingButton>
            </ControlActions>
        </RecordingControlsContainer>
    );
}

export default RecordingControls;
